import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { switchMap, catchError, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { isAuthDeadError } from '../auth/auth-errors';
import { getApiGatewayUrl } from '../config/auth0.config';
import { EnvironmentService, getSharedDomain } from '../config/environment.service';

/** ¿El host está bajo el dominio compartido de la plataforma?
 *
 *  En SG-2 **todo** lo que se sirve fuera vive en `<servicio>.<dominio>` y todo pasa por
 *  APISIX, que valida el JWT en TODAS las rutas sin una sola excepción (§4.3). Así que la
 *  pregunta "¿esto lleva Bearer?" ya no es una lista de URLs que hay que mantener servicio a
 *  servicio: es el dominio. Un servicio nuevo queda protegido el día que se despliega, sin
 *  tocar este fichero — que es justo lo que fallaba antes, con filter-builder y gotify
 *  saliendo sin token porque nadie los añadió a la lista.
 *
 *  ⚠️ El dominio se LEE de `runtime-env.js` (`getSharedDomain()`), no de un literal
 *  compilado: dev usa `shared.d.synapseguard.ai` y el literal viejo decía
 *  `shared.n.synapseguard.com` — la comparación nunca daba, así que "sin excepciones"
 *  llevaba semanas siendo "salvo TODO lo de dev que no estuviera también en
 *  `origenesExtra`" (solo el gateway GraphQL lo estaba). Ver `getSharedDomain()`.
 */
const esDominioCompartido = (hostname: string): boolean => {
  const dominio = getSharedDomain();
  return hostname === dominio || hostname.endsWith(`.${dominio}`);
};

/** Parsea una URL ABSOLUTA; `null` si es relativa (o inválida).
 *
 *  A propósito sin base: una relativa resuelta contra `location.origin` daría el host del
 *  propio frontend, que TAMBIÉN está bajo el dominio compartido — y entonces cada `.json`
 *  de i18n y cada asset se llevaría un Bearer.
 */
const urlAbsoluta = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const env = inject(EnvironmentService);

  // Orígenes EXTRA, fuera del dominio compartido: hoy solo el port-forward de APISIX en
  // desarrollo local (`http://localhost:9080`) y cualquier override que el despliegue meta
  // en runtime-env.js apuntando a otro sitio. Se comparan por origin completo (host +
  // puerto), no por substring: `req.url.includes(base)` daba positivos absurdos.
  const origenesExtra = [getApiGatewayUrl(), env.graphqlEndpoint]
    .map(u => urlAbsoluta(u)?.origin)
    .filter((o): o is string => !!o);

  const destino = urlAbsoluta(req.url);

  const needsToken = destino
    ? esDominioCompartido(destino.hostname) || origenesExtra.includes(destino.origin)
    // Relativa → mismo origin, o sea el nginx del propio frontend: assets, i18n y el
    // proxy `/kuma` de dev, que NO deben llevar token. Se conserva solo `/api/`, la
    // entrada legacy, para no romper una llamada invisible.
    : req.url.includes('/api/');

  if (!needsToken) {
    return next(req);
  }

  // Aquí NO se persiste nada. Hasta el 24-10-2026 un `tap` descodificaba el JWT y llamaba
  // a `TokenManagerService.saveTokenInfo()` en CADA petición: cada llamada escribía dos
  // claves NUEVAS en localStorage (`<prefijo>_exp_<ts>` y `<prefijo>_rcv_<ts>`) que nadie
  // leía y nadie borraba hasta el logout, así que una sesión larga acumulaba miles. La
  // caducidad del token la vigila SessionManagerService leyendo el JWT directamente.
  return authService.accessToken$.pipe(
    switchMap(token => {
      const authRequest = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
      return next(authRequest);
    }),
    catchError((err) => {
      // Solo capturar errores de obtención del token, NO errores HTTP del servidor
      if (err instanceof HttpErrorResponse) {
        return throwError(() => err);
      }
      console.warn('[AuthInterceptor] Failed to get access token for:', req.url, err?.message ?? err);
      // 401 SINTÉTICO (distinto de un 401 real del servidor), con dos sabores:
      //  - 'token_unavailable': código TERMINAL de auth0-spa-js — la sesión ya
      //    no puede emitir tokens. El errorInterceptor lo traduce a una
      //    recuperación de sesión muerta sin consultar la caché obsoleta de
      //    isAuthenticated$ (app#558).
      //  - 'token_fetch_failed': fallo transitorio (red, timeout). NADIE debe
      //    forzar re-login por un hipo: la capa de datos muestra su error y el
      //    siguiente request lo reintenta.
      const code = isAuthDeadError(err) ? 'token_unavailable' : 'token_fetch_failed';
      return throwError(() => new HttpErrorResponse({
        status: 401,
        statusText: 'Unauthorized',
        error: { code, message: 'Authentication token unavailable. Please log in again.' }
      }));
    })
  );
};
